import { GetServerSideProps, NextPage } from "next";
import App, { AppContext, AppProps } from "next/app";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";

import {
  prefetchUseSessionQuery,
  removeUseSessionQuery,
} from "@saas/account/data-access/@query/session/use-session.query.utils";
import { SESSION_COOKIE_KEY } from "@saas/account/data-access/fetch-session/fetch-session";
import { useSubaccountsQuery } from "@saas/account/data/@query/subaccounts/use-subaccounts.query";
import { usePermissions } from "@saas/account/feature/@hook/permissions/use-permissions";
import { useSessionProfile } from "@saas/account/feature/@hook/session-profile/use-session-profile";
import { AccountVerificationGuard } from "@saas/account/feature/account-verification-guard/account-verification-guard";
import { RouteGuard } from "@saas/account/feature/route-guard/route-guard";
import { trackAccountType } from "@saas/account/utils/analytics/analytics";
import { marketRoutes } from "@saas/config/dashboard/market-routes/market-routes";
import { publicRoutes } from "@saas/config/dashboard/public-routes/public-routes";
import { routeKeyLabelV2 } from "@saas/config/dashboard/route-key-label/route-key-label";
import { useNavigation } from "@saas/config/dashboard/use-navigation/use-navigation";
import { env } from "@saas/config/shared/env/env";
import { getUserRoleFromEmail } from "@saas/content/utils/get-user-role-from-email/get-user-role-from-email";
import { initAmplitude, setAmplitudeUser } from "@saas/core/amplitude";
import { queryClient } from "@saas/core/react-query/query-client/query-client";
import { prefetchUseFeatureFlagsStatusQuery } from "@saas/flags/data/@query/feature-flags-status/use-feature-flags-status.query.utils";
import { useFlagsStatus } from "@saas/flags/utils/@hook/flags-status/use-flags-status";
import { FlagKeys } from "@saas/flags/utils/@type/flag-keys/flag-keys";
import { Providers } from "@saas/layout/feature/providers/providers";
import { AppLayout } from "@saas/layout/ui/app-layout/app-layout";
import {
  prefetchMarketplaceConnectionsQuery,
  useMarketplaceConnectionsQuery,
} from "@saas/marketplace-connection/data-access/^queries/use-marketplace-connections-query/use-marketplace-connections-query";
import { registerNotificationUser } from "@saas/notification/data/register-notification-user/register-notification-user";
import { useOneSignal } from "@saas/notification/utils/@hook/one-signal/use-one-signal";
import { ErrorBoundary } from "@saas/shared/ui/error-boundary/error-boundary";
import { classNames } from "@saas/shared/utils/class-names/class-names";
import { isTest } from "@saas/shared/utils/environment/environment";
import {
  Breadcrumb,
  generateBreadcrumbs,
} from "@saas/shared/utils/generate-breadcrumbs/generate-breadcrumbs";

import "./styles.css";

import { dehydrate, DehydratedState } from "@tanstack/react-query";
import { getCookie } from "cookies-next";
import qs from "qs";
import { ParsedUrlQuery } from "querystring";
import { useIsClient } from "usehooks-ts";

const AccountMenu = dynamic(
  () =>
    import("@saas/account/feature/account-menu/account-menu").then((mod) => ({
      default: mod.AccountMenu,
    })),
  { ssr: true }
);

const UnverifiedAccountBlockerModal = dynamic(
  () =>
    import(
      "@saas/account/feature/unverified-account-blocker-modal/unverified-account-blocker-modal"
    ).then((mod) => ({
      default: mod.UnverifiedAccountBlockerModal,
    })),
  { ssr: true }
);
const VerificationNotification = dynamic(
  () =>
    import(
      "@saas/account/feature/verification-notification/verification-notification"
    ).then((mod) => ({
      default: mod.VerificationNotification,
    })),
  { ssr: true }
);

const Breadcrumbs = dynamic(
  () =>
    import("@saas/layout/ui/breadcrumbs/breadcrumbs").then((mod) => ({
      default: mod.Breadcrumbs,
    })),
  { ssr: true }
);
const UsetifulScript = dynamic(
  () =>
    import("@saas/layout/ui/usetiful/usetiful-script/usetiful-script").then(
      (mod) => ({
        default: mod.UsetifulScript,
      })
    ),
  { ssr: false }
);
const AuthorizationExpiredNotification = dynamic(
  () =>
    import(
      "@saas/marketplace-connection/ui/authorization-expired-notification/authorization-expired-notification"
    ).then((mod) => ({
      default: mod.AuthorizationExpiredNotification,
    })),
  { ssr: true }
);
const PushNotificationListener = dynamic(
  () =>
    import(
      "@saas/notification/feature/push-notification-listener/push-notification-listener"
    ).then((mod) => ({
      default: mod.PushNotificationListener,
    })),
  { ssr: false }
);

if (!isTest && env.API_MOCKING) {
  require("../../../tools/test/msw");
}

export type NextGetServerSideProps<
  Props = Record<string, unknown>,
  Query extends ParsedUrlQuery = ParsedUrlQuery
> = GetServerSideProps<Props & { dehydratedState: DehydratedState }, Query>;

export type NextPageWithLayout<Props = Record<string, unknown>> =
  NextPage<Props> & {
    getLayout?: (
      page: ReactElement,
      pageProps: Props & { dehydratedState: DehydratedState },
      flags: Record<FlagKeys, boolean>
    ) => ReactElement;
  };

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const DashboardLayout = ({
  children,
  statusCode,
}: {
  children: ReactElement;
  statusCode: number;
}) => {
  const router = useRouter();
  const flags = useFlagsStatus();
  const { profile, isHeadquarter } = useSessionProfile();
  const isClient = useIsClient();

  const { data: subaccountlist } = useSubaccountsQuery({
    page: 1,
    perPage: env.SUBBACOUNT_USER_LIMIT,
  });

  const { hasPermission, hasParentId } = usePermissions();

  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>(() =>
    generateBreadcrumbs({
      asPath: router.asPath,
      labelGenerator: (path) => routeKeyLabelV2[path],
    })
  );

  const verifiedStatus = useMemo(
    () => (profile?.emailVerified ? "verified" : "unverified"),
    [profile?.emailVerified]
  );

  const { data: marketplaceStatus } = useMarketplaceConnectionsQuery({
    select: (data) => (data?.length ? "connected" : "none"),
  });

  const { navigation } = useNavigation({
    defaultFlags: flags,
    isHeadquarter: isHeadquarter,
  });

  const isOrderCashier = useMemo(
    () => router.asPath.includes("/cashier"),
    [router.asPath]
  );

  const registerUser = useCallback(
    (oneSignalUserId: string) =>
      registerNotificationUser({
        deviceId: oneSignalUserId,
        deviceType: navigator.userAgent,
        isSubscribed: true,
      }),
    []
  );

  useOneSignal({
    enabled: !!env.ONESIGNAL_APP_ID,
    userId: profile?.id,
    onOneSignalUserIdReceived: registerUser,
  });

  useEffect(() => {
    setAmplitudeUser(profile?.id, {
      app_name: "Melaka Web App",
    });

    if (subaccountlist && profile?.id) {
      const haveSubaccount =
        !profile?.parentUserId && subaccountlist.length > 0 ? true : false;

      const accountLevel = !profile?.parentUserId
        ? "parent_account"
        : "sub_account";

      trackAccountType(profile?.id, haveSubaccount, accountLevel);
    }
  }, [profile?.id, profile?.parentUserId, subaccountlist]);

  useEffect(() => {
    if (isClient) {
      const [title] = window.document.title?.split("|") ?? [];

      setBreadcrumbs(
        generateBreadcrumbs({
          title,
          asPath: router.asPath,
          labelGenerator: (path) => routeKeyLabelV2[path],
        })
      );
    }
  }, [isClient, router.asPath]);

  return (
    <>
      <UsetifulScript
        userId={profile?.id}
        userRole={getUserRoleFromEmail({ email: profile?.email })}
        verifiedStatus={verifiedStatus}
        marketplaceStatus={marketplaceStatus}
        token={env.USETIFUL_TOKEN}
      />

      <RouteGuard>
        <AccountVerificationGuard>
          <>
            <AppLayout
              accountMenu={() => <AccountMenu />}
              navigation={navigation}
              unverifiedAccountBlockerModal={() => (
                <UnverifiedAccountBlockerModal />
              )}
              enabledNotificationR2={false}
              isVerified={profile?.emailVerified}
              accesses={profile?.accesses}
              hasAccess={hasPermission(router.asPath)}
              hasParentId={hasParentId}
              statusCode={statusCode}
              organizationName={profile?.organizationName}
              branchName={profile?.branchName}
              branchType={profile?.branchType}
            >
              <div
                className={classNames(
                  isOrderCashier
                    ? "bg-neutral-N0 md:bg-neutral-N100 min-h-screen p-0"
                    : "bg-neutral-N100 min-h-full p-5 md:p-6",
                  "flex flex-1 flex-col gap-6"
                )}
              >
                {profile ? <VerificationNotification /> : null}

                <AuthorizationExpiredNotification />

                {breadcrumbs.length ? (
                  <Breadcrumbs breadcrumbs={breadcrumbs} />
                ) : null}

                {children}
              </div>
            </AppLayout>
            <PushNotificationListener />
          </>
        </AccountVerificationGuard>
      </RouteGuard>
    </>
  );
};

const DashboardApp = ({
  Component,
  pageProps,
}: AppPropsWithLayout): ReactElement => {
  useEffect(() => {
    initAmplitude();
  }, []);

  const getLayout = useCallback(
    (page, pageProps) => {
      return (
        <Providers dehydratedState={pageProps.dehydratedState}>
          {({ flags }) =>
            Component.getLayout ? (
              Component.getLayout(page, pageProps, flags)
            ) : (
              <DashboardLayout statusCode={pageProps.statusCode}>
                {page}
              </DashboardLayout>
            )
          }
        </Providers>
      );
    },
    [Component]
  );

  return getLayout(
    <ErrorBoundary>
      <Component {...pageProps} />
    </ErrorBoundary>,
    pageProps
  );
};

DashboardApp.getInitialProps = async (context: AppContext) => {
  const { req, res, pathname, err } = context.ctx;

  const statusCode = res ? res.statusCode : err ? err.statusCode : 404;

  const isServer = req && res;

  if (isServer) {
    const { cookie } = req.headers;
    const sessionCookieValue = getCookie(SESSION_COOKIE_KEY, {
      req,
      res,
    }) as string;

    const isPublicRoute = [...publicRoutes, ...marketRoutes].includes(pathname);

    if (!sessionCookieValue && !isPublicRoute && res.writeHead) {
      const queryString = qs.stringify(
        {
          // If the user is not logged in and visiting private route, we'll redirect them to the login page.
          redirect: req.url === "/" ? "/order" : req.url,
        },
        { addQueryPrefix: true }
      );

      removeUseSessionQuery();
      res.writeHead(307, {
        Location: `/login${queryString}`,
      });

      res.end();
    }

    await Promise.allSettled([
      prefetchUseFeatureFlagsStatusQuery(),
      prefetchUseSessionQuery(sessionCookieValue),
      prefetchMarketplaceConnectionsQuery(cookie),
    ]);
  }

  const appProps = await App.getInitialProps(context);

  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      dehydratedState: dehydrate(queryClient),
      statusCode: statusCode,
    },
  };
};

export default DashboardApp;
